import {
  Analyst,
  Reporting,
  ReportingGlobalStatistics,
  ReportingPeriod,
  ReportingPeriodStatistics,
} from "@tudigo-monorepo/core-tudigo-api-models";
import { CollectionRequestParams } from "@tudigo-monorepo/web-tudigo-api-client";

import { ContentType, Request, RequestParams, TudigoResponse } from "../api";

export const reportingQueryFields =
  "id," +
  "name," +
  "status," +
  "reporting_campaign.fields(id,organization.fields(name,business_categories,company_information.fields(legal_name,legal_status,registration_numbers.fields(siret),head_office_address.fields(formatted))))," +
  "analysis_writer.fields(id,firstname,lastname,profile_image,position)," +
  "analysis," +
  "trust_level," +
  "period_start_date," +
  "period_end_date," +
  "fill_date," +
  "reviewed_date," +
  "is_warning_required," +
  "validated_date," +
  "first_reminder_date," +
  "manual_reminder_date," +
  "last_reminder_date," +
  "next_scheduled_reminder_date," +
  "should_send_reminder_mail," +
  "reminder_notifications," +
  "reporter_comment," +
  "is_reporter_comment_public," +
  "internal_comment," +
  "data," +
  "timeseries," +
  "my_investments.fields(id,total_amount_in_cents,payment_date)," +
  "form_properties_to_display," +
  "reporting_documents," +
  "should_display_not_filled_reporting," +
  "should_send_investor_limit_date_reached_email," +
  "related_projects.fields(id,content.fields(name,logo,cover),total_amount_collected_in_cents,fundraising_progress_min_goal,fundraising_progress_max_goal,finished_at)";

export type GetUserReportingsFilters = {
  periodStartDate?: string;
  organizationId?: string;
  refId?: string;
};

export type GetUserReportingsQueryParams = CollectionRequestParams<
  { userId: string },
  GetUserReportingsFilters
>;

export const reportingsEndpoints = (request: Request) => ({
  getOrganizationReportings: (
    organizationId: string,
    query?: {
      fields?: string;
      offset?: number;
      limit?: number;
      filters?: any;
    },
    params: RequestParams = {},
  ) =>
    request<TudigoResponse<Reporting[]>, void>({
      path: `/api/v1/organizations/${organizationId}/reportings`,
      method: "GET",
      query,
      secure: true,
      type: ContentType.Json,
      ...params,
    }),

  getReportings: (
    query?: {
      fields?: string;
      offset?: number;
      limit?: number;
      filters?: any;
    },
    params: RequestParams = {},
  ) =>
    request<TudigoResponse<Reporting[]>, void>({
      path: `/api/v1/reportings`,
      method: "GET",
      query,
      secure: true,
      type: ContentType.Json,
      ...params,
    }),

  getReportingById: ({
    reportingId,
    query,
    params,
  }: {
    reportingId: Reporting["id"];
    query?: Record<string, string>;
    params?: RequestParams;
  }) => {
    return request<TudigoResponse<Reporting>, void>({
      path: `/api/v1/reportings/${reportingId}`,
      method: "GET",
      query,
      secure: true,
      type: ContentType.Json,
      ...params,
    });
  },

  updateReporting: (
    reportingId: Reporting["id"],
    body: Reporting,
    query?: {
      fields?: string;
    },
    params: RequestParams = {},
  ) =>
    request<TudigoResponse<Reporting>, void>({
      path: `/api/v2/reportings/${reportingId}`,
      method: "PUT",
      body: body,
      query: query,
      secure: true,
      type: ContentType.Json,
      ...params,
    }),

  getAnalysts: (
    query?: {
      fields?: string;
      offset?: number;
      limit?: number;
      filters?: any;
    },
    params: RequestParams = {},
  ) =>
    request<TudigoResponse<Analyst[]>, void>({
      path: `/api/v1/analysts`,
      method: "GET",
      query,
      secure: true,
      type: ContentType.Json,
      ...params,
    }),

  publishReporting: (
    reportingId: string,
    publishParams: {
      shouldSendPublicationNotification: boolean;
      shouldSendPublicationMail: boolean;
    },
    params: RequestParams = {},
  ) =>
    request<TudigoResponse<Reporting>, void>({
      path: `/api/v1/reportings/${reportingId}/publish`,
      method: "POST",
      secure: true,
      body: publishParams,
      type: ContentType.Json,
      ...params,
    }),

  unpublishReporting: (reportingId: string, params: RequestParams = {}) =>
    request<TudigoResponse<Reporting>, void>({
      path: `/api/v1/reportings/${reportingId}/unpublish`,
      method: "POST",
      secure: true,
      type: ContentType.Json,
      body: {},
      ...params,
    }),

  sendReminder: (reportingId: string, params: RequestParams = {}) =>
    request<TudigoResponse<Reporting>, void>({
      path: `/api/v1/reportings/${reportingId}/send-reminder`,
      method: "POST",
      secure: true,
      type: ContentType.Json,
      ...params,
    }),

  updateReportingSettings: (
    reportingId: string,
    data: {
      shouldSendReminderMail?: boolean;
      shouldSendInvestorLimitDateReachedEmail?: boolean;
      shouldDisplayNotFilledReporting?: boolean;
    },
    params: RequestParams = {},
  ) =>
    request<TudigoResponse<Reporting>, void>({
      path: `/api/v1/reportings/${reportingId}/settings`,
      method: "PUT",
      body: data,
      secure: true,
      type: ContentType.Json,
      ...params,
    }),

  getReportingsStatistics: (
    query?: {
      fields?: string;
      from?: string;
      to?: string;
    },
    params: RequestParams = {},
  ) =>
    request<TudigoResponse<ReportingGlobalStatistics>, void>({
      path: "/api/v1/reportings-statistics",
      method: "GET",
      query,
      secure: true,
      type: ContentType.Json,
      ...params,
    }),

  getReportingsStatisticsByPeriod: (
    query?: {
      fields?: string;
      from?: string;
      to?: string;
    },
    params: RequestParams = {},
  ) =>
    request<TudigoResponse<ReportingPeriodStatistics>, void>({
      path: "/api/v1/reportings-statistics/period",
      method: "GET",
      query,
      secure: true,
      type: ContentType.Json,
      ...params,
    }),

  getReportingsPeriodList: (
    query?: { fields?: string },
    params: RequestParams = {},
  ) =>
    request<TudigoResponse<Array<ReportingPeriod>>, void>({
      path: "/api/v1/reportings-statistics/periods/list",
      method: "GET",
      query,
      secure: true,
      type: ContentType.Json,
      ...params,
    }),

  getUserReportings: (
    queryParams: GetUserReportingsQueryParams,
    requestParams: RequestParams = {},
  ) => {
    const { pathParams, ...query } = queryParams;

    return request<TudigoResponse<Reporting[]>, void>({
      path: `/api/v1/users/${pathParams?.userId}/reportings`,
      method: "GET",
      query: {
        ...query.filters,
        ...query.pagination,
        ...query.sorting,
        fields: query.fields,
      },
      secure: true,
      type: ContentType.Json,
      ...requestParams,
    });
  },
});
